import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Maximize, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { fileUrl } from "../api";

const ProductCard = ({ product, showSpecs = false }) => {
  const images = (product.images && product.images.length ? product.images : [product.image]).filter(Boolean);
  const [idx, setIdx] = useState(0);
  const touchX = useRef(null);
  const multi = images.length > 1;

  const go = (e, dir) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setIdx((i) => (i + dir + images.length) % images.length);
  };
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null || !multi) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(null, dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <Link
      to={`/onze-luchtkastelen/${product.slug}`}
      className="group flex flex-col rounded-3xl bg-white border border-slate-100 shadow-sm hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300 overflow-hidden animate-fade-up"
    >
      <div
        className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-brand-bluelight to-slate-50"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid={`card-gallery-${product.slug}`}
      >
        <img
          src={fileUrl(images[idx])}
          alt={product.name}
          loading="lazy"
          className={product.has_vullingen ? "w-full h-full object-contain p-3" : "w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"}
        />
        <span className="absolute top-3 left-3 text-xs font-bold text-brand-blue bg-white/95 backdrop-blur rounded-full px-3 py-1 shadow-sm">
          {product.category}
        </span>
        <span data-testid={`badge-nieuw-${product.slug}`} className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs font-bold text-white bg-brand-green rounded-full px-3 py-1 shadow-sm">
          <Sparkles className="w-3.5 h-3.5" /> Gloednieuw
        </span>

        {multi && (
          <>
            <button
              type="button"
              onClick={(e) => go(e, -1)}
              aria-label="Vorige foto"
              data-testid={`card-prev-${product.slug}`}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-brand-blue hover:bg-brand-bluedark text-white flex items-center justify-center shadow-md opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity z-10"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={(e) => go(e, 1)}
              aria-label="Volgende foto"
              data-testid={`card-next-${product.slug}`}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-brand-blue hover:bg-brand-bluedark text-white flex items-center justify-center shadow-md opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity z-10"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-2.5 inset-x-0 flex justify-center gap-1.5">
              {images.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-white" : "w-1.5 bg-white/60"}`} />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display text-lg text-brand-navy group-hover:text-brand-blue transition-colors">{product.name}</h3>
        <p className="text-sm text-slate-500 mt-1 line-clamp-2">{product.tagline}</p>

        <div className="flex items-center gap-4 mt-3 text-sm text-brand-navy">
          <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-brand-blue" /> {product.capacity}</span>
          <span className="inline-flex items-center gap-1.5"><Maximize className="w-4 h-4 text-brand-blue" /> {product.size}</span>
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
          <span className="font-bold text-brand-navy">
            vanaf <span className="text-brand-pink font-display text-lg">€{product.price}</span> / dag
            <span className="block text-[11px] font-medium text-slate-400">excl. bezorgkosten</span>
          </span>
          <span className="inline-flex items-center gap-1 text-brand-blue font-bold text-sm group-hover:gap-2 group-hover:text-brand-pink transition-all">
            Bekijk <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
